import { forwardRef, useMemo } from "react"
import { IconButtonProps, styled, SvgIcon, SvgIconProps } from "@mui/material"
import { classes } from "@tm/utils"
import { DefaultSvg } from "./default-svg"

type ColorType = "primary" | "secondary" | "warning" | "error" | "info" | "success"

export type IconProps = Omit<SvgIconProps, "color"> & {
    /**
     * The id of the reference in the spritemap
     */
    name?: string
    /**
     * The CSS width and height assuming it's a square. If the width and height must be different, you should use sx={} instead.
     * If you want to use sizes like small, medium, large, you have to use fontSize={}
     */
    size?: IconButtonProps["size"] | "inherit"
    /**
     * The Mui palette color or CSS color which will be set an to the fill color. If it's not defined, fill will be set to currentColor
     */
    color?: ColorType | "highlight" | string
}

const StyledSvgIcon = styled(SvgIcon, {
    shouldForwardProp: (prop: string) => !["name", "color", "size"].includes(prop),
})<Omit<IconProps, "name">>(({ color, theme, size = "medium" }) => {
    const paletteColors = ["primary", "secondary", "warning", "error", "info", "success", "highlight"]
    const themeColor = paletteColors.some((x) => x === color) ? theme.palette[color as ColorType]?.main : color

    return {
        fontSize: "1em",
        width: "auto",
        objectFit: "contain",
        fill: themeColor ?? "currentColor",

        ...(size === "extrasmall" && {
            fontSize: 12,
        }),

        ...(size === "small" && {
            fontSize: 16,
        }),

        ...(size === "medium" && {
            fontSize: 20,
        }),

        ...(size === "large" && {
            fontSize: 24,
        }),

        ...(size === "extralarge" && {
            fontSize: 32,
        }),
    }
})

export const Icon = forwardRef<SVGSVGElement, IconProps>((props, ref) => {
    const { name, className, ...rest } = props

    const svgClassName = useMemo(() => {
        return classes(`icon-${name}`, className)
    }, [name, className])

    if (!name) {
        return <DefaultSvg ref={ref} {...(rest as Omit<IconProps, "color">)} />
    }

    return (
        <StyledSvgIcon ref={ref} className={svgClassName} {...(rest as Omit<IconProps, "color">)}>
            <use xlinkHref={`#${name}`} />
        </StyledSvgIcon>
    )
})
